import React, { useEffect, useMemo, useState } from "react";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import {
  BoardColumn,
  ProjectTask,
  TaskPriority,
  TaskStatus,
  TaskType,
  TASK_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
} from "../../../../interfaces/ProjectTask";
import {
  createTask,
  updateTask,
  deleteTask,
} from "../../../../services/projectTaskApi";
import MultiSelect from "../../../../components/searchableSelect/MultiSelect";

interface EmployeeOption {
  id: number;
  name: string;
}

interface Props {
  open: boolean;
  projectId: number;
  employees: EmployeeOption[];
  tasks: ProjectTask[];
  /** Colunas do board, para o seletor de status (inclui customizadas). */
  columns: BoardColumn[];
  editing: ProjectTask | null;
  /** Tipo pré-selecionado ao criar (vindo da árvore: "+ tarefa" num épico etc.). */
  defaultType?: number;
  /** Tarefa pai pré-selecionada ao criar. */
  defaultParentId?: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const TaskModal: React.FC<Props> = ({
  open,
  projectId,
  employees,
  tasks,
  columns,
  editing,
  defaultType,
  defaultParentId,
  onClose,
  onSaved,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [estimatedHours, setEstimatedHours] = useState<string>("");
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [status, setStatus] = useState<number>(TaskStatus.Backlog);
  const [priority, setPriority] = useState<number>(TaskPriority.Medium);
  const [dependsOn, setDependsOn] = useState<number[]>([]);
  const [scheduleLocked, setScheduleLocked] = useState(false);
  const [parentTaskId, setParentTaskId] = useState<string>("");
  const [type, setType] = useState<number>(TaskType.Task);
  const [saving, setSaving] = useState(false);

  // Tarefa com filhos: a estimativa é derivada (soma das filhas) — não editável manualmente.
  const hasChildren = !!editing && tasks.some((t) => t.parentTaskId === editing.id);

  // Descendentes de quem está sendo editado: não podem ser o pai dela (viraria ciclo, e o roll-up
  // de estimativa sobe a cadeia de pais — ciclo ali não deixa a árvore feia, pendura o request).
  const descendants = useMemo(() => {
    if (!editing) return new Set<number>();
    const out = new Set<number>();
    let frontier = [editing.id];
    while (frontier.length) {
      const next: number[] = [];
      for (const id of frontier)
        for (const t of tasks)
          if (t.parentTaskId === id && !out.has(t.id)) { out.add(t.id); next.push(t.id); }
      frontier = next;
    }
    return out;
  }, [editing, tasks]);

  // Candidatos = qualquer tarefa do projeto que não seja ela mesma nem descendente dela. A
  // profundidade é livre, então a única exclusão que sobra é a que evitaria ciclo. O TIPO se
  // ajusta ao destino: a tela não explica a regra, ela lista o que é possível e diz no que
  // vai converter.
  const LEVEL = { [TaskType.Epic]: 0, [TaskType.Task]: 1, [TaskType.Subtask]: 2 } as Record<number, number>;
  const parentCandidates = tasks.filter((t) => {
    if (t.projectId !== projectId) return false;
    if (editing && (t.id === editing.id || descendants.has(t.id))) return false;
    return true;
  });

  // Em que a tarefa se converte ao cair no pai escolhido — anunciado ANTES de salvar, porque
  // converter tipo é mudar dado sem pedido explícito, e descobrir depois é pior que a recusa.
  const parentSelecionado = tasks.find((t) => String(t.id) === parentTaskId);
  const tipoResultante = parentSelecionado
    ? ((LEVEL[parentSelecionado.type] ?? 1) + 1 === 2 ? TaskType.Subtask : TaskType.Task)
    : type === TaskType.Epic
    ? TaskType.Epic
    : TaskType.Task;

  useEffect(() => {
    if (editing) {
      setTitle(editing.title);
      setDescription(editing.description ?? "");
      setEstimatedHours(editing.estimatedHours?.toString() ?? "");
      setAssigneeIds(
        editing.assignees && editing.assignees.length > 0
          ? editing.assignees.map((a) => a.id)
          : editing.assigneeEmployeeId
          ? [editing.assigneeEmployeeId]
          : []
      );
      setStatus(editing.status);
      setPriority(editing.priority);
      setDependsOn(editing.dependsOnTaskIds ?? []);
      setScheduleLocked(editing.scheduleLocked ?? false);
      setParentTaskId(editing.parentTaskId != null ? String(editing.parentTaskId) : "");
      setType(editing.type ?? TaskType.Task);
    } else {
      setTitle("");
      setDescription("");
      setEstimatedHours("");
      setAssigneeIds([]);
      setStatus(TaskStatus.Backlog);
      setPriority(TaskPriority.Medium);
      setDependsOn([]);
      setScheduleLocked(false);
      setType(defaultType ?? TaskType.Task);
      setParentTaskId(defaultParentId != null ? String(defaultParentId) : "");
    }
  }, [editing, open, defaultType, defaultParentId]);

  if (!open) return null;

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const payload = {
      projectId,
      title: title.trim(),
      description: description || undefined,
      estimatedHours: estimatedHours ? Number(estimatedHours) : null,
      assigneeEmployeeIds: assigneeIds,
      status,
      priority,
      dependsOnTaskIds: dependsOn,
      scheduleLocked,
      type,
      parentTaskId: type === TaskType.Epic || !parentTaskId ? null : Number(parentTaskId),
    };
    try {
      if (editing) {
        await updateTask({ ...payload, id: editing.id });
      } else {
        await createTask(payload);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      // Antes daqui só saía um console.error: qualquer 4xx/5xx virava "cliquei em salvar e não
      // aconteceu nada", com o modal aberto e sem pista nenhuma do motivo.
      console.error("Erro ao salvar tarefa", e);
      toast.error(
        typeof e?.response?.data === "string"
          ? e.response.data
          : e?.response?.data?.message ?? "Erro ao salvar tarefa."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm("Excluir esta tarefa?")) return;
    setSaving(true);
    try {
      await deleteTask(editing.id);
      onSaved();
      onClose();
    } catch (e) {
      console.error("Erro ao excluir tarefa", e);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white";
  const labelCls = "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";
  const hintCls = "mt-1 text-[11px] text-gray-700 dark:text-gray-400";

  return (
    <Modal onClose={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[20px] border border-gray-100 bg-white p-6 shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            {editing ? "Editar tarefa" : "Nova tarefa"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-lg p-1 text-gray-700 transition-colors hover:bg-gray-100 hover:text-navy-700 dark:text-gray-300 dark:hover:bg-navy-700 dark:hover:text-white"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="task-title" className={labelCls}>
              Título <span className="text-red-500">*</span>
            </label>
            <input
              id="task-title"
              className={inputCls}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Implementar tela de login"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="task-description" className={labelCls}>
              Descrição
            </label>
            <textarea
              id="task-description"
              className={inputCls}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="task-type" className={labelCls}>
              Tipo
            </label>
            <select
              id="task-type"
              className={inputCls}
              value={type}
              onChange={(e) => {
                const novo = Number(e.target.value);
                setType(novo);
                // Épico não tem pai; nos outros casos o pai é PRESERVADO — o tipo se ajusta ao
                // destino, não o contrário. Zerar aqui (comportamento antigo) fazia a pessoa
                // perder o vínculo em silêncio só por encostar no seletor de tipo.
                if (novo === TaskType.Epic) setParentTaskId("");
              }}
            >
              {Object.entries(TASK_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
            <p className={hintCls}>Épico → Tarefa → Subtarefa.</p>
          </div>

          <div>
            <label htmlFor="task-parent" className={labelCls}>
              {type === TaskType.Epic ? "Pai" : "Vincular a"}
            </label>
            <select
              id="task-parent"
              className={inputCls}
              value={parentTaskId}
              onChange={(e) => setParentTaskId(e.target.value)}
              disabled={type === TaskType.Epic}
            >
              <option value="">
                {type === TaskType.Epic ? "— Épico não tem pai —" : "— Sem pai —"}
              </option>
              {parentCandidates.map((t) => (
                <option key={t.id} value={t.id}>
                  {TASK_TYPE_LABELS[t.type] ?? "Tarefa"} · {t.title}
                </option>
              ))}
            </select>

            {/* Converter tipo é mudar dado sem pedido explícito. Anunciar antes de salvar é o que
                separa "o sistema traduziu meu pedido" de "o sistema mexeu no meu dado". */}
            {type !== TaskType.Epic && tipoResultante !== type && (
              <p className={hintCls}>
                Ao salvar, esta tarefa vira <strong>{TASK_TYPE_LABELS[tipoResultante]}</strong> —
                o tipo acompanha o nível do pai escolhido.
              </p>
            )}

            {/*
              Lista vazia é indistinguível de bug se nada explicar o porquê — foi assim que "não
              consigo vincular" nasceu.
            */}
            {type !== TaskType.Epic && parentCandidates.length === 0 && (
              <p className={hintCls}>
                Não há outra tarefa neste projeto para ser o pai desta.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="task-estimate" className={labelCls}>
              Estimativa (horas)
            </label>
            <input
              id="task-estimate"
              type="number"
              min={0}
              step={0.5}
              className={inputCls}
              value={estimatedHours}
              onChange={(e) => setEstimatedHours(e.target.value)}
              disabled={hasChildren}
            />
            {hasChildren && (
              <p className={hintCls}>
                Calculada automaticamente pela soma das estimativas das subtarefas.
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Responsáveis</label>
            <MultiSelect
              placeholder="— Sem responsável —"
              value={assigneeIds}
              onChange={(v) => setAssigneeIds(v.map((x) => Number(x)))}
              options={employees.map((e) => ({ label: e.name, value: e.id }))}
            />
            <p className={hintCls}>
              Pode ter vários. O primeiro é o responsável principal (usado no cronograma/capacity).
            </p>
          </div>

          <div>
            <label htmlFor="task-status" className={labelCls}>
              Status
            </label>
            <select
              id="task-status"
              className={inputCls}
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
            >
              {[...columns]
                .sort((a, b) => a.position - b.position)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label htmlFor="task-priority" className={labelCls}>
              Prioridade
            </label>
            <select
              id="task-priority"
              className={inputCls}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
            >
              {Object.entries(TASK_PRIORITY_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-white/10 dark:bg-navy-900">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-dv-blue-500"
                checked={scheduleLocked}
                onChange={(e) => setScheduleLocked(e.target.checked)}
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-navy-700 dark:text-white">Fixar cronograma manualmente</span> — o cronograma automático
                não recalcula esta tarefa (ela ainda consome o capacity nos dias definidos).
              </span>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Depende de (pré-requisitos)</label>
            <MultiSelect
              placeholder="Selecione as tarefas…"
              value={dependsOn}
              onChange={(v) => setDependsOn(v.map((x) => Number(x)))}
              options={tasks
                .filter((t) => !editing || t.id !== editing.id)
                .map((t) => ({ label: t.title, value: t.id }))}
            />
            <p className={hintCls}>
              Clique no “×” de cada item para remover.
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          <div>
            {editing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-600 transition-all hover:bg-red-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
              >
                Excluir
              </button>
            )}
          </div>
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !title.trim()}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-6 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
            >
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TaskModal;
